import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { logUxEvent } from '../telemetry/uxLogger';
import {
  installSoftKeyboardWatcher,
  detectSoftKeyboardOpen,
  SOFT_KEYBOARD_EVENT,
} from '../utils/softKeyboard';

const BACKEND_URL = process.env.REACT_APP_API_URL || window.location.origin;
const STORAGE_KEY_SESSION = 'support_chat_session';
const STORAGE_KEY_POS = 'support_chat_position';
const STORAGE_KEY_HINT_DISMISSED = 'support_chat_hint_dismissed';
const STORAGE_KEY_FAB_DISMISSED = 'support_chat_fab_dismissed';
const PRIMARY = '#D32F2F';
const PRIMARY_DARK = '#B71C1C';
const SUPPORT_EMAIL = 'atendimento@consultamais.app';
// Handoff humano: apenas e-mail (WhatsApp da empresa desativado).

/**
 * O sessionId passou a ser o portador da fila humana: quem apresenta o id em
 * GET /support/chat/replies recebe o que a equipe escreveu naquela conversa.
 * `Math.random()` não é criptográfico, então sessões novas usam
 * `crypto.getRandomValues` (128 bits). Sessões antigas continuam válidas — o
 * id já está no localStorage do paciente e trocá-lo perderia a conversa.
 */
function createSessionId() {
  try {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function getSessionId() {
  let id = localStorage.getItem(STORAGE_KEY_SESSION);
  if (!id) {
    id = createSessionId();
    localStorage.setItem(STORAGE_KEY_SESSION, id);
  }
  return id;
}

function getSavedPos() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY_POS)); } catch { return null; }
}

function savePos(pos) {
  try { localStorage.setItem(STORAGE_KEY_POS, JSON.stringify(pos)); } catch {}
}

function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }

function formatTime(ts) {
  if (!ts) return '';
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

// Auditoria UX 10/10/2026 (sessão 3bed4af3…): com o botão de e-mail já na
// tela, o paciente respondeu "Sim" à oferta de envio e seguiu escrevendo.
// A Sofia não envia e-mail por ele; quem envia é o botão. Confirmação curta
// nesse estado não vai ao backend: destaca o botão.
const HANDOFF_CONFIRM_RX = /^(sim|s|ok|okay|pode|pode sim|pode ser|pode enviar|pode mandar|envia|envie|manda|mande|quero|quero sim|isso|confirmo|claro|certo|pronto|beleza|por favor|sim,? por favor)[\s.!,]*$/i;
const HANDOFF_CONFIRM_NUDGE = 'Para a equipe receber, toque em “Enviar e-mail para a equipe”, logo abaixo. O e-mail já sai com o histórico desta conversa.';

export const isHandoffConfirmation = (text) => HANDOFF_CONFIRM_RX.test(String(text || '').trim());

const HANDOFF_EMAIL_MAX_BODY = 7000;
// Espelha SUPPORT_MAX_USER_MESSAGES do backend (functions/index.js): o 31º
// turno do paciente é recusado lá com a resposta de limite. Manter o teto aqui
// evita que o paciente digite de novo só para receber o aviso de limite.
const MAX_USER_MESSAGES = 30;
// Fila humana: de quanto em quanto tempo o widget busca o que a equipe
// escreveu no painel /suporte. 10s é rápido o suficiente para parecer conversa
// e lento o suficiente para não pesar (1 leitura de documento por ciclo).
const TEAM_REPLY_POLL_MS = 10000;

function readPatientStorage(key) {
  try {
    return (sessionStorage.getItem(key) || localStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
}

/**
 * Tela atual, só o pathname. Query e hash carregam patientId/e-mail e ficam
 * de fora. Sem isso a Sofia respondia no escuro: "o iniciar consulta não
 * aparece pra mim" vindo da triagem virava chamado de falha técnica em vez
 * de orientação de onde responder (caso Rafaella 29/09/2026).
 */
function readCurrentRoute() {
  try {
    return window.location.pathname || '';
  } catch {
    return '';
  }
}

/** Dados do cadastro/triagem no mesmo navegador (quando existirem). */
function getPatientContextFromStorage() {
  return {
    name: readPatientStorage('patientName'),
    email: readPatientStorage('patientEmail'),
    cpf: readPatientStorage('patientCpf'),
    phone: readPatientStorage('patientPhone'),
    route: readCurrentRoute(),
  };
}

const EMAIL_RX = /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/i;

/** E-mail confirmado na conversa (paciente informou) ou cadastro local. */
function resolveHandoffEmail(messages, patientContext) {
  const fromStorage = (patientContext?.email || '').trim();
  if (Array.isArray(messages)) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m?.role === 'user') {
        const match = String(m.content || '').match(EMAIL_RX);
        if (match) return match[0];
      }
    }
  }
  return fromStorage;
}

/**
 * Yahoo Mail (app) corrompe %XX do mailto quando o hex tem A, C ou E
 * (incidente Renata 20/10/2026: "Olá" → "Ol%3%1"). O href sai em ASCII.
 */
export function foldMailtoAscii(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2013\u2014\u2015]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');
}

/**
 * Monta e-mail do paciente para atendimento@consultamais.app com histórico
 * completo do webchat (handoff = continuidade por e-mail, não só o endereço).
 */
export function buildWebchatHandoffEmail({ messages, sessionId, patientContext }) {
  const patient = patientContext || getPatientContextFromStorage();
  const handoffEmail = resolveHandoffEmail(messages, patient);
  const transcriptLines = (Array.isArray(messages) ? messages : [])
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && String(m.content || '').trim())
    .map((m) => {
      const label = m.role === 'user' ? 'Eu' : 'Sofia';
      const time = formatTime(m.timestamp);
      const prefix = time ? `[${time}] ` : '';
      return `${prefix}${label}:\n${String(m.content).trim()}`;
    });

  const meta = [];
  if (sessionId) meta.push(`Sessão do chat: ${sessionId}`);
  if (patient.name) meta.push(`Nome: ${patient.name}`);
  if (handoffEmail) meta.push(`E-mail para retorno: ${handoffEmail}`);
  else if (patient.email) meta.push(`E-mail cadastrado: ${patient.email}`);
  if (patient.cpf) meta.push(`CPF: ${patient.cpf}`);
  if (patient.phone) meta.push(`Telefone: ${patient.phone}`);

  const parts = [
    'Olá, equipe Consulta+,',
    '',
    'Gostaria de continuar o atendimento iniciado no chat do site. Segue o histórico da conversa com a Sofia:',
    '',
    '———',
    ...meta,
    ...(meta.length && transcriptLines.length ? [''] : []),
    ...(transcriptLines.length ? [transcriptLines.join('\n\n'), ''] : []),
    '———',
    '',
    'Minha mensagem / complemento:',
    '',
  ];

  let body = parts.join('\n');
  if (body.length > HANDOFF_EMAIL_MAX_BODY) {
    body =
      body.slice(0, HANDOFF_EMAIL_MAX_BODY) +
      '\n\n[... histórico truncado por limite do app de e-mail — nossa equipe também registra o chat no painel de suporte]';
  }

  const subject = patient.name
    ? `Atendimento Consulta+ — ${patient.name}`
    : 'Atendimento Consulta+ — continuação do chat';

  const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(foldMailtoAscii(subject))}&body=${encodeURIComponent(foldMailtoAscii(body))}`;

  return { to: SUPPORT_EMAIL, subject, body, href, replyEmail: handoffEmail || patient.email || '' };
}

/**
 * Props:
 *   embedded=false    Modo iframe: dialog ocupa 100% do container, sem FAB,
 *                     sem drag, sem sticky-payBar. Usado em /embed/sofia.
 *   defaultOpen=false Abre o widget já expandido (útil no modo embedded).
 */
export default function SupportChatWidget({ embedded = false, defaultOpen = false } = {}) {
  const [open, setOpen] = useState(defaultOpen || embedded);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [handoff, setHandoff] = useState(false);
  // A equipe assumiu a conversa pelo painel /suporte. Enquanto isso, as
  // respostas dela chegam aqui pelo polling e a Sofia fica calada (o backend
  // suprime a IA — ver webchatHumanTakeoverActive).
  const [teamActive, setTeamActive] = useState(false);
  // Feedback visual para o link "E-mail" no handoff. Telemetria mostrou 41
  // rage clicks/dia: paciente clica esperando ação visível, mas em mobiles
  // sem app de email padrão configurado o mailto: aparenta não fazer nada.
  // Copiamos o corpo do e-mail (histórico + complemento) para o clipboard
  // e mostramos "Copiado!" por 2.4s — fallback para colar no webmail.
  const [emailCopied, setEmailCopied] = useState(false);
  const [emailPulse, setEmailPulse] = useState(false);
  const emailPulseTimer = useRef(null);
  const handoffBoxRef = useRef(null);
  const [pos, setPos] = useState(() => getSavedPos() || { right: 24, bottom: 24 });
  const [dragging, setDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  // Quando há um CTA fixo de pagamento na tela (ConsultaChat: .pay-top-bar
  // sticky bottom), o widget Sofia precisa subir para não cobrir o botão Pagar
  // — caso contrário o <strong>Pergunte aqui</strong> intercepta o clique do
  // paciente que tenta pagar.
  const [stickyPayBarHeight, setStickyPayBarHeight] = useState(0);
  // Quando o teclado virtual está aberto (mobile), encolhemos o FAB para fora
  // da viewport — efeito visual equivalente a "o teclado cobriu o widget" —
  // e ajustamos a altura do dialog para que ele não fique atrás do teclado.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [vvHeight, setVvHeight] = useState(
    typeof window !== 'undefined' && window.visualViewport
      ? window.visualViewport.height
      : (typeof window !== 'undefined' ? window.innerHeight : 0)
  );
  // Hint "Dúvidas? Pergunte aqui" próximo ao FAB. Aparece quando o widget está fechado,
  // ainda não foi dispensado e o usuário nunca abriu o chat. Some ao primeiro clique
  // no hint, no FAB, ou ao botão de fechar do hint.
  const [showHint, setShowHint] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_HINT_DISMISSED) !== '1';
    } catch {
      return true;
    }
  });
  const [fabDismissed, setFabDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_FAB_DISMISSED) === '1';
    } catch {
      return false;
    }
  });
  // Aberto por gatilho externo (ex.: paywall com falha na geração do Pix).
  // Eleva o z-index do dialog acima do .paywall-overlay (9999), que na ordem
  // normal do DOM cobriria o widget.
  const [elevated, setElevated] = useState(false);
  const dismissHint = useCallback(() => {
    setShowHint(false);
    try { localStorage.setItem(STORAGE_KEY_HINT_DISMISSED, '1'); } catch {}
  }, []);

  // Permite que qualquer tela abra o widget programaticamente:
  // window.dispatchEvent(new CustomEvent('cm:open-support-chat')).
  useEffect(() => {
    const onOpenRequest = () => {
      setElevated(true);
      setOpen(true);
      dismissHint();
    };
    window.addEventListener('cm:open-support-chat', onOpenRequest);
    return () => window.removeEventListener('cm:open-support-chat', onOpenRequest);
  }, [dismissHint]);

  useEffect(() => {
    if (!open) setElevated(false);
  }, [open]);

  const dismissFab = useCallback(() => {
    setFabDismissed(true);
    setOpen(false);
    dismissHint();
    try { localStorage.setItem(STORAGE_KEY_FAB_DISMISSED, '1'); } catch {}
  }, [dismissHint]);
  const dragStart = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragTarget = useRef(null);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const btnRef = useRef(null);
  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const afterHandoffSends = useRef(0);
  // Fila humana: marca d'água da última resposta da equipe já exibida, para o
  // polling pedir só o que chegou depois e nunca duplicar mensagem na tela.
  const lastTeamReplyAt = useRef(0);
  const teamPollBusy = useRef(false);

  const patientContext = useMemo(() => getPatientContextFromStorage(), [open]);

  const userMessageCount = useMemo(
    () => messages.filter((m) => m?.role === 'user').length,
    [messages]
  );
  const capReached = userMessageCount >= MAX_USER_MESSAGES;
  // O handoff não fecha o canal: a saída por e-mail passa a conviver com o
  // campo de digitar. Só o teto de mensagens encerra a conversa automática.
  const showEmailHandoff = handoff || capReached;

  const handoffEmail = useMemo(() => {
    if (!showEmailHandoff) return null;
    return buildWebchatHandoffEmail({ messages: messages.filter((m) => !m.local), sessionId: sessionId.current, patientContext });
  }, [showEmailHandoff, messages, patientContext]);

  useEffect(() => {
    if (open) setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [messages, open]);

  // Lembra qual elemento abriu o widget para restaurar o foco ao fechar.
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (open) {
      restoreFocusRef.current =
        (typeof document !== 'undefined' && document.activeElement) || null;
      setTimeout(() => inputRef.current?.focus(), 200);
    } else if (restoreFocusRef.current) {
      // O FAB lançador só é renderizado quando !open, então o nó capturado em
      // restoreFocusRef foi desmontado ao abrir e está destacado do DOM. Damos
      // o foco ao lançador recém-remontado (btnRef); se ele não existir mais
      // (ex.: FAB dispensado), caímos no elemento que originalmente abriu o widget.
      const launcher = btnRef.current;
      const fallback = restoreFocusRef.current;
      const target =
        (launcher && typeof launcher.focus === 'function' && launcher) ||
        (fallback && typeof fallback.focus === 'function' && fallback) ||
        null;
      if (target) {
        try { target.focus(); } catch {}
      }
      restoreFocusRef.current = null;
    }
  }, [open]);

  // Escape fecha o widget; Tab + Shift+Tab faz focus trap dentro do modal.
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const root = modalRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!root.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Observa a presença e a posição do CTA Pagar fixo (.pay-top-bar) para
  // reposicionar o widget acima dele somente quando há sobreposição real
  // — i.e., quando o pay-top-bar está sticky-grudado no fundo da viewport
  // ou seu rodapé encosta na zona do widget Sofia. Sem isso, o balão
  // "Dúvidas? Pergunte aqui" sobrepõe o botão Pagar e captura o clique
  // do paciente.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    let resizeObserver = null;
    let activePayBar = null;
    let rafId = null;

    // Procura, em ordem de prioridade: a barra Pagar sticky do chat pago
    // (.pay-top-bar) e, em rotas onde ela não existe, o wrapper do CTA
    // principal de pagamento (.continue-wrap em /pagamento). Assim o FAB
    // sobe acima do botão Pagar/Continuar mesmo fora do chat.
    // Na Home (/home) o mesmo tratamento vale para .home-sticky-cta, a barra
    // "Iniciar consulta agora" fixa no rodapé em mobile — o observer só a
    // considera quando ela está realmente visível (body tem a classe
    // .show-home-sticky-cta), evitando reservar espaço à toa quando o CTA
    // do hero ainda está na tela.
    const findCtaTarget = () => {
      const homeSticky = document.querySelector('.home-sticky-cta');
      const homeStickyVisible = homeSticky &&
        document.body.classList.contains('show-home-sticky-cta');
      return (
        document.querySelector('.pay-top-bar') ||
        document.querySelector('.continue-wrap') ||
        (homeStickyVisible ? homeSticky : null) ||
        document.querySelector('.btn-accept') ||
        document.querySelector('.anamnese-page .composer')
      );
    };

    const measure = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const target = findCtaTarget();
        if (!target) {
          setStickyPayBarHeight((prev) => (prev === 0 ? prev : 0));
          return;
        }
        const rect = target.getBoundingClientRect();
        const vh = window.innerHeight || 0;

        // Zona do widget Sofia: bolha (56px) + buffer do balão (24px) acima
        // da borda inferior do FAB. Se o CTA intersecta essa zona, sobe o FAB
        // até a borda inferior ficar 24px acima do TOPO do CTA. Compensar pelo
        // topo (e não pela altura do CTA) cobre o caso em que a barra sticky
        // não encosta no fundo da viewport (ex.: iPhone com rodapé de suporte
        // abaixo da barra Pagar), onde a compensação pela altura deixava o FAB
        // atrás do botão Pagar (toque caía no Pagar, x inalcançável).
        const fabBottom = vh - pos.bottom;
        const sofiaZoneTop = fabBottom - (56 + 24);
        const intersects = rect.top < fabBottom && rect.bottom > sofiaZoneTop;
        const raise = intersects
          ? Math.max(0, Math.round(fabBottom - rect.top + 24))
          : 0;
        setStickyPayBarHeight((prev) => (prev === raise ? prev : raise));
      });
    };

    const attachResize = (target) => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      activePayBar = target || null;
      if (target && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => measure());
        resizeObserver.observe(target);
      }
    };

    const refresh = () => {
      const target = findCtaTarget();
      if (target !== activePayBar) attachResize(target);
      measure();
    };

    refresh();

    const mutation = new MutationObserver(refresh);
    // Observa também `class` do body: `.home-sticky-cta` fica montada o tempo
    // todo, mas só entra em cena quando body ganha `show-home-sticky-cta`.
    // Sem observar a mudança de classe, o FAB não reagiria a esse toggle.
    mutation.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      mutation.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos.bottom]);

  // Quando há barra Pagar sticky no rodapé, dispensar o hint para reduzir
  // ruído visual e impedir que o balão de texto bloqueie cliques.
  useEffect(() => {
    if (stickyPayBarHeight > 0 && showHint) dismissHint();
  }, [stickyPayBarHeight, showHint, dismissHint]);

  // Detecta abertura do teclado virtual via visualViewport. Quando o paciente
  // foca um input num formulário ou na triagem, o teclado entra e o FAB sai
  // de cena (translate-out). Sem esse listener, o widget cobre parte do
  // teclado / input e atrapalha a digitação.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    let raf = null;
    installSoftKeyboardWatcher();
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = vv.height;
        // Navegador: 80px de tolerância para o jitter do visualViewport no iOS.
        // App Android: a janela inteira encolhe, ver `utils/softKeyboard`.
        const isOpen = detectSoftKeyboardOpen();
        setVvHeight(h);
        setKeyboardOpen((prev) => (prev === isOpen ? prev : isOpen));
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('orientationchange', update);
    window.addEventListener(SOFT_KEYBOARD_EVENT, update);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener(SOFT_KEYBOARD_EVENT, update);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  // Fila humana do webchat, volta: enquanto a conversa estiver na fila do
  // painel /suporte, busca o que a equipe escreveu e mostra aqui. Antes disto
  // o handoff era só de ida — o caso urgente entrava na fila e a equipe não
  // tinha por onde responder no chat, o que forçava a ponte de e-mail.
  useEffect(() => {
    if (!open || !handoff) return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled || teamPollBusy.current) return;
      teamPollBusy.current = true;
      try {
        const url = `${BACKEND_URL}/support/chat/replies`
          + `?sessionId=${encodeURIComponent(sessionId.current)}`
          + `&since=${lastTeamReplyAt.current}`;
        const res = await fetch(url);
        const data = await res.json();
        if (cancelled || !data?.ok) return;
        // Sessão sem conversa no painel (handoff normal não abre ticket):
        // desliga o polling em vez de perguntar no vazio para sempre.
        if (data.queued === false) {
          if (timer) clearInterval(timer);
          return;
        }
        if (data.humanMode) setTeamActive(true);
        const incoming = Array.isArray(data.replies) ? data.replies : [];
        if (!incoming.length) return;
        lastTeamReplyAt.current = incoming[incoming.length - 1].at;
        setTeamActive(true);
        setMessages((prev) => [
          ...prev,
          ...incoming.map((r) => ({
            role: 'assistant',
            content: r.content,
            fromTeam: true,
            timestamp: { _seconds: r.at / 1000 },
          })),
        ]);
        try {
          logUxEvent('support_chat_team_reply_received', {
            count: incoming.length,
            embedded,
          });
        } catch {}
      } catch {
        // Rede instável: tenta no próximo ciclo, sem quebrar o chat.
      } finally {
        teamPollBusy.current = false;
      }
    };

    poll();
    timer = setInterval(poll, TEAM_REPLY_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [open, handoff, embedded]);

  const handleSend = async () => {
    if (!input.trim() || sending || capReached) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    if (handoff) {
      afterHandoffSends.current += 1;
      try {
        logUxEvent('support_chat_continued_after_handoff', {
          sends_after_handoff: afterHandoffSends.current,
          user_message_count: userMessageCount + 1,
          chars: text.length,
          embedded,
        });
      } catch {}
    }

    const newMessages = [...messages, { role: 'user', content: text, timestamp: { _seconds: Date.now() / 1000 } }];

    if (handoff && !teamActive && isHandoffConfirmation(text)) {
      setMessages([
        ...newMessages,
        { role: 'assistant', content: HANDOFF_CONFIRM_NUDGE, local: true, timestamp: { _seconds: Date.now() / 1000 } },
      ]);
      setSending(false);
      setEmailPulse(true);
      if (emailPulseTimer.current) clearTimeout(emailPulseTimer.current);
      emailPulseTimer.current = setTimeout(() => setEmailPulse(false), 2400);
      try { handoffBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch {}
      try {
        logUxEvent('support_chat_handoff_confirm_nudged', {
          sends_after_handoff: afterHandoffSends.current,
          embedded,
        });
      } catch {}
      return;
    }

    setMessages(newMessages);

    try {
      const history = newMessages.filter(m => !m.local).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${BACKEND_URL}/support/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId.current,
          message: text,
          history,
          // Rota lida agora, não na abertura do widget: o paciente pode ter
          // mudado de tela com a Sofia aberta.
          patientContext: { ...patientContext, route: readCurrentRoute() },
        }),
      });
      const data = await res.json();
      // Equipe no comando: o backend devolve reply vazio de propósito (a Sofia
      // não escreve por cima do humano). A resposta vem pelo polling.
      if (data.humanMode) {
        setTeamActive(true);
        setHandoff(true);
      }
      if (data.ok && data.reply) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.reply, timestamp: { _seconds: Date.now() / 1000 } },
        ]);
        if (data.handoff) setHandoff(true);
      }
    } catch { /* vazio */ } finally {
      setSending(false);
    }
  };

  const startDrag = (e, target) => {
    const touch = e.touches?.[0] || e;
    const rect = (target === 'btn' ? btnRef : modalRef).current?.getBoundingClientRect();
    if (!rect) return;
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    dragTarget.current = target;
    setHasDragged(false);
    setDragging(true);
  };

  const onDrag = useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const touch = e.touches?.[0] || e;
    const dx = Math.abs(touch.clientX - dragStart.current.x);
    const dy = Math.abs(touch.clientY - dragStart.current.y);
    if (dx > 4 || dy > 4) setHasDragged(true);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const elW = dragTarget.current === 'btn' ? 56 : 360;
    const elH = dragTarget.current === 'btn' ? 56 : 480;
    const newRight = clamp(vw - touch.clientX - elW + dragOffset.current.x, 0, vw - 60);
    const newBottom = clamp(vh - touch.clientY - elH + dragOffset.current.y, 0, vh - 60);
    setPos({ right: newRight, bottom: newBottom });
  }, [dragging]);

  const endDrag = useCallback(() => {
    if (dragging) { setDragging(false); savePos(pos); }
  }, [dragging, pos]);

  useEffect(() => {
    if (!dragging) return;
    const opts = { passive: false };
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchmove', onDrag, opts);
    window.addEventListener('touchend', endDrag);
    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('touchmove', onDrag);
      window.removeEventListener('touchend', endDrag);
    };
  }, [dragging, onDrag, endDrag]);

  const handleFabClick = () => {
    if (!hasDragged) {
      setOpen(true);
      dismissHint();
    }
  };

  // O dialog e o FAB sao renderizados como irmaos para que o FAB persista no
  // DOM mesmo quando o widget esta aberto. Isso permite que o foco volte
  // corretamente ao launcher quando o dialogo fecha (Escape ou botao X).
  const dialogStyleEmbedded = {
    position: 'fixed', zIndex: 9999, inset: 0,
    width: '100vw', height: '100dvh',
    maxWidth: '100vw', maxHeight: '100dvh',
    borderRadius: 0, border: 'none', boxShadow: 'none',
    background: '#fff',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  };
  const dialogStyleFloating = {
    position: 'fixed', zIndex: elevated ? 10005 : 9999, width: 360, maxWidth: 'calc(100vw - 16px)',
    height: 480,
    maxHeight: keyboardOpen
      ? `${Math.max(220, vvHeight - 16)}px`
      : 'calc(100dvh - 16px)',
    borderRadius: 20,
    background: '#fff', boxShadow: '0 12px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    border: '1px solid rgba(0,0,0,0.06)',
    right: typeof window !== 'undefined' ? clamp(pos.right, 4, window.innerWidth - 368) : pos.right,
    bottom: keyboardOpen
      ? clamp(8, 4, Math.max(4, vvHeight - 220))
      : (typeof window !== 'undefined'
          ? clamp(pos.bottom + stickyPayBarHeight, 4, Math.max(4, window.innerHeight - 488))
          : pos.bottom),
    cursor: dragging ? 'grabbing' : 'default',
    userSelect: dragging ? 'none' : 'auto',
  };
  const replyEmail = handoffEmail?.replyEmail;
  const emailInstruction = replyEmail ? (
    <>Envie um e-mail de <strong>{replyEmail}</strong> para {SUPPORT_EMAIL}</>
  ) : (
    <>Envie um e-mail para {SUPPORT_EMAIL} a partir da sua conta</>
  );
  // Com a equipe já respondendo aqui, o e-mail deixa de ser o caminho e passa
  // a ser alternativa — inverter isso na copy evita mandar o paciente para o
  // e-mail no exato momento em que tem um humano do outro lado do chat.
  const handoffNotice = capReached ? (
    <>Esta conversa automática chegou ao limite de mensagens. {emailInstruction}. Toque abaixo — o histórico já vem preenchido.</>
  ) : teamActive ? (
    <>A equipe está respondendo por aqui. Escreva no campo abaixo. Se preferir o e-mail: {emailInstruction} — o histórico já vem preenchido.</>
  ) : (
    <>Para falar com a equipe: {emailInstruction}. Toque abaixo — o histórico já vem preenchido. Se preferir, você também pode continuar escrevendo aqui.</>
  );

  const dialogNode = open ? (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-chat-title"
      style={embedded ? dialogStyleEmbedded : dialogStyleFloating}
    >
        {/* Header */}
        <div
          style={{
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            color: '#fff', padding: '14px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: embedded ? 'default' : 'grab', flexShrink: 0,
            borderBottom: '1px solid var(--rule-soft, rgba(181,138,79,0.45))',
          }}
          onMouseDown={embedded ? undefined : (e) => startDrag(e, 'modal')}
          onTouchStart={embedded ? undefined : (e) => startDrag(e, 'modal')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)',
            }}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/sofia-avatar.png`}
                alt=""
                width={36}
                height={36}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div>
              <div
                id="support-chat-title"
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: 1.2,
                  letterSpacing: '0.01em',
                }}
              >
                Sofia
              </div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>Assistente virtual</div>
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '4px 8px',
                borderRadius: 8,
                minWidth: 32, minHeight: 32,
              }}
              aria-label="Fechar conversa com Sofia"
            >&times;</button>
          )}
        </div>

        {/* Messages */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#FAFBFC' }}>
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex',
            flexDirection: 'column', gap: 6, WebkitOverflowScrolling: 'touch',
          }}>
            {messages.length === 0 && !sending && (
              <div style={{
                background: '#fff', padding: '12px 14px', borderRadius: 14,
                fontSize: 13, color: '#111827', lineHeight: 1.4,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #F3F4F6',
              }}>
                <p style={{ margin: 0, lineHeight: 1.5 }}>
                  Olá! Sou a Sofia, assistente virtual da Consulta+. Em que posso ajudar?
                </p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                {msg.role === 'assistant' && (
                  // Quem está falando precisa ficar explícito: a resposta da
                  // equipe humana não pode se passar por resposta da IA.
                  <span style={{
                    fontSize: 10,
                    color: msg.fromTeam ? PRIMARY : '#6B7280',
                    fontWeight: 600, marginBottom: 2, marginLeft: 4,
                  }}>
                    {msg.fromTeam ? 'Equipe Consulta+' : 'Sofia'}
                  </span>
                )}
                <div style={{
                  maxWidth: '82%', padding: '10px 14px', borderRadius: 16, wordBreak: 'break-word',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  ...(msg.role === 'user'
                    ? { background: PRIMARY, color: '#fff', borderBottomRightRadius: 4 }
                    : { background: '#fff', border: '1px solid #F3F4F6', borderBottomLeftRadius: 4 }),
                }}>
                  <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                </div>
                <span style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, padding: '0 4px' }}>{formatTime(msg.timestamp)}</span>
              </div>
            ))}
            {sending && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, marginBottom: 2, marginLeft: 4 }}>Sofia</span>
                <div style={{
                  maxWidth: '82%', padding: '12px 18px', borderRadius: 16, borderBottomLeftRadius: 4,
                  background: '#fff', border: '1px solid #F3F4F6', opacity: 0.8,
                }}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {[0, 1, 2].map(n => (
                      <span key={n} style={{
                        width: 7, height: 7, borderRadius: '50%', background: '#9CA3AF',
                        animation: `supportDot 1.2s ease-in-out ${n * 0.2}s infinite`,
                      }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {showEmailHandoff && (
            <div
              ref={handoffBoxRef}
              style={{
                borderTop: '1px solid #E5E7EB',
                padding: '12px 14px',
                background: '#FFFBEB',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: '#92400E',
                  lineHeight: 1.4,
                  textAlign: 'center',
                  fontWeight: 500,
                }}
              >
                {handoffNotice}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {/*
                  Botao WhatsApp (contato da empresa) removido: canal desativado.
                */}
                <a
                  href={handoffEmail?.href || `mailto:${SUPPORT_EMAIL}`}
                  onClick={(ev) => {
                    // Auditoria UX 28/06: 42 rage clicks em 6 sessoes iOS quando
                    // o chat caia — mailto: nao da feedback imediato e o paciente
                    // clica varias vezes. Bloqueamos cliques apos o primeiro
                    // (pointer-events:none + preventDefault) ate o cooldown.
                    if (emailCopied) {
                      ev.preventDefault();
                      return;
                    }
                    const copyText = handoffEmail?.body || SUPPORT_EMAIL;
                    try {
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(copyText).catch(() => {});
                      }
                    } catch {}
                    setEmailCopied(true);
                    // 4s — paciente em mobile tem tempo de ver instrucao e tentar colar.
                    setTimeout(() => setEmailCopied(false), 4000);
                  }}
                  style={{
                    flex: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '10px 12px',
                    minHeight: 42,
                    borderRadius: 10,
                    background: emailCopied ? '#16A34A' : PRIMARY,
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    textDecoration: 'none',
                    boxShadow: emailPulse ? '0 0 0 4px rgba(245, 158, 11, 0.6)' : 'none',
                    transform: emailPulse ? 'scale(1.03)' : 'none',
                    transition: 'background-color 0.18s ease, box-shadow 0.25s ease, transform 0.25s ease',
                    pointerEvents: emailCopied ? 'none' : 'auto',
                  }}
                  onMouseOver={(e) => { if (!emailCopied) e.currentTarget.style.backgroundColor = PRIMARY_DARK; }}
                  onMouseOut={(e) => { if (!emailCopied) e.currentTarget.style.backgroundColor = PRIMARY; }}
                  aria-label={`Abrir e-mail para ${SUPPORT_EMAIL} com histórico da conversa (clique também copia o texto)`}
                  aria-disabled={emailCopied || undefined}
                >
                  {emailCopied ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      E-mail copiado
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Enviar e-mail para a equipe
                    </>
                  )}
                </a>
              </div>
              {emailCopied && (
                <div
                  role="status"
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: '#92400E',
                    lineHeight: 1.35,
                    textAlign: 'center',
                  }}
                >
                  Se o app de e-mail não abrir sozinho, cole o texto em qualquer e-mail novo para {SUPPORT_EMAIL}.
                </div>
              )}
            </div>
          )}
          {!capReached && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              style={{
                borderTop: '1px solid #E5E7EB', padding: '10px 12px', display: 'flex', gap: 8,
                background: '#fff', flexShrink: 0,
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={teamActive
                  ? 'Escreva para a equipe'
                  : (handoff ? 'Continue escrevendo aqui' : 'Digite aqui')}
                aria-label="Digite aqui sua mensagem para Sofia"
                disabled={sending}
                enterKeyHint="send"
                autoComplete="off"
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 12, border: '1.5px solid #E5E7EB',
                  fontSize: 14, outline: 'none', fontFamily: 'inherit',
                }}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                style={{
                  width: 42, height: 42, borderRadius: 12, border: 'none',
                  background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                  color: '#fff', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  opacity: sending || !input.trim() ? 0.4 : 1,
                }}
                aria-label="Enviar mensagem"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </form>
          )}

        </div>

        <style>{`
          @keyframes supportDot {
            0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
            40% { opacity: 1; transform: scale(1.1); }
          }
        `}</style>
    </div>
  ) : null;

  return (
    <>
      <style>{`
        @keyframes support-hint-in {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .support-fab-hint { animation: none !important; }
        }
      `}</style>

      {dialogNode}

      {!embedded && showHint && !fabDismissed && !dragging && !open && !keyboardOpen && (
        <div
          className="support-fab-hint"
          role="note"
          aria-label="Dúvidas? Pergunte aqui"
          style={{
            position: 'fixed',
            zIndex: 9997,
            right: pos.right + 64,
            bottom: pos.bottom + 8 + stickyPayBarHeight,
            maxWidth: 'calc(100vw - 96px)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px 8px 14px',
            borderRadius: 999,
            background: '#FFFFFF',
            border: '1px solid rgba(0,0,0,0.06)',
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, Inter, Roboto, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            color: '#1E1E1E',
            cursor: 'pointer',
            animation: 'support-hint-in .35s cubic-bezier(.4,0,.2,1) both',
            whiteSpace: 'nowrap',
            WebkitTapHighlightColor: 'transparent',
          }}
          onClick={() => { setOpen(true); dismissHint(); }}
        >
          <span>
            Dúvidas? <strong style={{ color: PRIMARY, fontWeight: 600 }}>Pergunte aqui</strong>
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); dismissHint(); }}
            aria-label="Fechar dica"
            style={{
              marginLeft: 2,
              // 24px é o alvo mínimo da WCAG 2.5.8. O fundo é transparente,
              // então crescer só amplia a área de toque, sem mudar o visual.
              width: 24,
              height: 24,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              color: '#9CA3AF',
              fontSize: 16,
              lineHeight: 1,
              cursor: 'pointer',
              padding: 0,
              borderRadius: 999,
            }}
          >
            ×
          </button>
        </div>
      )}

      {!embedded && !fabDismissed && !open && (
        <div
          className="support-fab"
          style={{
            position: 'fixed',
            zIndex: 9998,
            right: pos.right,
            bottom: pos.bottom,
            width: 56,
            height: 56,
            // A subida por causa da barra Pagar sai por `transform`, não por
            // `bottom`: animar `bottom` é layout shift e entrava no CLS (3º
            // maior ofensor do app na auditoria de 29/09/2026, 2.338
            // atribuições). Transform não conta para o CLS.
            transform: keyboardOpen
              ? 'translateY(140%)'
              : (stickyPayBarHeight > 0 ? `translateY(-${stickyPayBarHeight}px)` : 'none'),
            opacity: keyboardOpen ? 0 : 1,
            pointerEvents: keyboardOpen ? 'none' : 'auto',
            transition: dragging
              ? 'none'
              : 'transform 0.22s cubic-bezier(.4,0,.2,1), opacity 0.18s ease',
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); dismissFab(); }}
            aria-label="Dispensar chat de suporte"
            title="Fechar"
            style={{
              position: 'absolute',
              // 24px (alvo mínimo da WCAG 2.5.8) com o deslocamento ajustado
              // para o círculo ficar centrado no mesmo ponto de antes.
              top: -5,
              right: -5,
              zIndex: 2,
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              color: '#6B7280',
              fontSize: 15,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ×
          </button>
          <button
            type="button"
            ref={btnRef}
            onClick={handleFabClick}
            onMouseDown={(e) => startDrag(e, 'btn')}
            onTouchStart={(e) => startDrag(e, 'btn')}
            aria-hidden={keyboardOpen ? 'true' : undefined}
            tabIndex={keyboardOpen ? -1 : 0}
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: dragging ? 'grabbing' : 'pointer',
            }}
            title="Dúvidas? Pergunte aqui"
            aria-label="Abrir chat de suporte com Sofia, assistente virtual"
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
}
